import { ButtonLink } from "./Button";
import { sellerSubmitUrl } from "../lib/seller";
import styles from "./FounderInvite.module.css";
import type { ReactNode } from "react";

/**
 * The founder-facing half of an empty state (`WEB-016`).
 *
 * A visitor arriving before Dealbid has inventory reads an honest explanation addressed to
 * a **buyer** — who is the one visitor an empty board cannot help. The founder reading the
 * same screen is the one who can change it, and before this nothing on the page asked them
 * to. This is that ask, and it is deliberately the *second* thing in the state: the page is
 * a buyer's page even when it is empty, so the invitation sits below the explanation, in
 * muted type, behind a rule.
 *
 * **What it may offer is fixed by canon, and it is short.** The permanent Founding badge
 * (canon § 4), a permanent credit on the Founding Deals wall for the first 10 approved
 * deals (decision `D-009`), and cold-start exposure while eligible inventory is at most ten
 * (canon § 6). Those are things Dealbid controls and can therefore keep.
 *
 * **What it must not offer is the reason this copy is centralised.** Canon § 1 declines to
 * promise sales, leads or a particular impression count in the product thesis, and
 * marketing copy is exactly where that promise creeps back in. The mechanism is stated
 * plainly — a founder posts a genuine deal, buyers read the board, and whoever wants the
 * deal opens it on the founder's own site — and then the sentence that refuses to promise
 * anyone will. `test/components/founder-invite.test.tsx` holds that as a regression.
 *
 * **No countdown of remaining places.** `AGENTS.md` § 11 forbids fake scarcity, and a
 * hardcoded remaining count is false the moment the first deal is approved. The wall's own
 * contents show how full the cohort is; `D-009` states the cohort and never counts it down.
 * "First 10" is canon § 4's cohort of approved **deals** — never "the first 10 bidders",
 * which is a different and smaller set, since a deal can be approved and never bid.
 *
 * There is no timer, no invented figure and no urgency device of any kind.
 */
export function FounderInvite({ lead }: { lead: ReactNode }) {
  return (
    <div className={styles.invite}>
      {/*
        A paragraph rather than a heading. The invitation is secondary content inside a
        state that already has a heading above it, and a level-3 title under a level-1
        board heading would put a gap in the document outline for the sake of styling.
      */}
      <p className={styles.title}>Are you a software founder?</p>
      <p className={styles.body}>{lead}</p>
      <p className={styles.body}>
        The first 10 deals Dealbid approves are Founding Deals. They keep that badge
        permanently, and they are named permanently on the Founding Deals wall at the foot
        of this page. While Dealbid has ten or fewer eligible deals, an approved deal stays
        on the board rather than dropping off as the day turns over.
      </p>
      <p className={styles.body}>
        The mechanism is the whole of it: you post a genuine deal, buyers read the board,
        and whoever wants your deal opens it on your own site. Dealbid does not promise that
        anyone will.
      </p>
      {/*
        The same destination the masthead and the auction section use (`WEB-014`), built by
        `src/lib/seller.ts` so the founder invite code rides along on the one link that
        leaves for Dealbid's own seller app. A second path invented here would be a second
        thing to keep in step.
      */}
      <ButtonLink className={styles.action} variant="secondary" href={sellerSubmitUrl()}>
        Submit a deal
      </ButtonLink>
    </div>
  );
}
